import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { AvailableDeliverySlots } from '../../../client/services';

type SlotInfo = {
  slot: string;
  maxPoints: number;
  usedPoints: number;
  remainingPoints: number;
  isAvailable: boolean;
};

type CapacityLevel = 'plenty' | 'filling' | 'almostFull';

const LEVEL_META: Record<
  CapacityLevel,
  { color: string; track: string; label: string }
> = {
  plenty: {
    color: '#43A047',
    track: 'rgba(67, 160, 71, 0.18)',
    label: 'Plenty',
  },
  filling: {
    color: '#FB8C00',
    track: 'rgba(251, 140, 0, 0.18)',
    label: 'Filling',
  },
  almostFull: {
    color: '#E53935',
    track: 'rgba(229, 57, 53, 0.16)',
    label: 'Low',
  },
};

const formatTodayForApi = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTodayLabel = (): string =>
  new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const formatSlotLabel = (slot: string): string =>
  String(slot || '').replace(/\s+to\s+/i, ' – ');

const getBookedPercent = (usedPoints: number, maxPoints: number): number => {
  if (!maxPoints || maxPoints <= 0) return 0;
  return Math.min(100, Math.max(0, (usedPoints / maxPoints) * 100));
};

const getSlotBookedPercent = (slot: SlotInfo): number => {
  if (!slot.isAvailable || slot.remainingPoints <= 0) return 100;
  return getBookedPercent(slot.usedPoints, slot.maxPoints);
};

const getCapacityLevel = (slot: SlotInfo): CapacityLevel => {
  if (!slot.isAvailable || slot.remainingPoints <= 0) return 'almostFull';
  const bookedPct = getBookedPercent(slot.usedPoints, slot.maxPoints);
  if (bookedPct > 85) return 'almostFull';
  if (bookedPct >= 50) return 'filling';
  return 'plenty';
};

interface Props {
  headerAction?: ReactNode;
}

const TodayAvailableSlots = ({ headerAction }: Props) => {
  const [slots, setSlots] = useState<SlotInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const todayApi = formatTodayForApi();

  const loadSlots = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await AvailableDeliverySlots.find({
        query: {
          date: todayApi,
          points: 0,
          acceptance: false,
        },
      });

      setSlots(
        (response?.slots || []).map((item: any) => ({
          slot: String(item.slot || ''),
          maxPoints: Number(item.maxPoints) || 0,
          usedPoints: Number(item.usedPoints) || 0,
          remainingPoints: Number(item.remainingPoints) || 0,
          isAvailable:
            typeof item.isAvailable === 'boolean'
              ? item.isAvailable
              : Number(item.remainingPoints) > 0,
        }))
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load available slots');
      setSlots([]);
    } finally {
      setIsLoading(false);
    }
  }, [todayApi]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: 3,
        borderRadius: 2.5,
        bgcolor: '#FFFFFF',
        border: '1px solid',
        borderColor: 'rgba(33, 43, 54, 0.08)',
        boxShadow: '0 1px 3px rgba(33, 43, 54, 0.04)',
      }}
    >
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              color: '#2F2A27',
              lineHeight: 1.3,
            }}
          >
            Available slots — today
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: '#7A7470', mt: 0.2, fontSize: '0.8rem' }}
          >
            {formatTodayLabel()}
          </Typography>
        </Box>
        {headerAction ? (
          <Box sx={{ flexShrink: 0, ml: 'auto' }}>{headerAction}</Box>
        ) : null}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : error ? (
        <Typography
          variant='body2'
          color='error'
        >
          {error}
        </Typography>
      ) : slots.length === 0 ? (
        <Typography
          variant='body2'
          color='text.secondary'
        >
          No delivery slots configured for today.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 1.25,
              width: '100%',
            }}
          >
            {slots.map((slot) => {
              const level = getCapacityLevel(slot);
              const meta = LEVEL_META[level];
              const bookedPct = Math.round(getSlotBookedPercent(slot));

              return (
                <Box
                  key={slot.slot}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.35,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'rgba(33, 43, 54, 0.1)',
                    bgcolor: '#FFFFFF',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                    }}
                  >
                    <CircularProgress
                      variant='determinate'
                      value={100}
                      size={44}
                      thickness={4.5}
                      sx={{
                        color: `${meta.track} !important`,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                      }}
                    />
                    <CircularProgress
                      variant='determinate'
                      value={bookedPct}
                      size={44}
                      thickness={4.5}
                      sx={{
                        color: `${meta.color} !important`,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        [`& .MuiCircularProgress-circle`]: {
                          strokeLinecap: 'round',
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          color: '#2F2A27',
                          lineHeight: 1,
                        }}
                      >
                        {bookedPct}%
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        color: '#5C5652',
                        lineHeight: 1.25,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {formatSlotLabel(slot.slot)}
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.65,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: meta.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          fontWeight: 500,
                          color: '#6B6560',
                          lineHeight: 1.25,
                        }}
                      >
                        {slot.isAvailable
                          ? `${slot.remainingPoints} left of ${slot.maxPoints}`
                          : 'Full'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Stack
            direction='row'
            spacing={2.5}
            justifyContent='flex-start'
            flexWrap='wrap'
            useFlexGap
            sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed rgba(33, 43, 54, 0.08)' }}
          >
            {[
              { color: '#43A047', label: 'Available (<50%)' },
              { color: '#FB8C00', label: 'Filling Up (50–85%)' },
              { color: '#E53935', label: 'Almost Full (>85%)' },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: item.color,
                  }}
                />
                <Typography sx={{ fontSize: '0.78rem', color: '#7A7470' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default TodayAvailableSlots;
